import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Button, Callout, Spinner, TextField, type SelectFieldOption } from '@humanforce/mango-ui-react';
import { PageTitle } from '../components/layout/PageTitle';
import { MangoSelectField } from '../features/workers/components/MangoSelectField';
import { RetroactiveReasonField } from '../features/workers/components/RetroactiveReasonField';
import { createWorker, getReferenceData, getWorker, updateWorkerDetail, type ReferenceData } from '../features/workers/workersApi';
import { GENDER_LABELS, type CreatePersonRequest, type Gender } from '../features/workers/workersApiTypes';
import { hasErrors, isRetroactive, validatePersonDetail, type FieldErrors } from '../features/workers/workersValidation';
import { trimOrUndefined } from '../features/workers/workersUtils';

interface FormState {
  title: string;
  legalFirstName: string;
  legalMiddleName: string;
  legalLastName: string;
  preferredFirstName: string;
  pronouns: string;
  dateOfBirth: string;
  gender: string;
  globalEmployeeId: string;
  effectiveFrom: string;
  retroactiveReason: string;
}

const EMPTY_FORM: FormState = {
  title: '', legalFirstName: '', legalMiddleName: '', legalLastName: '', preferredFirstName: '',
  pronouns: '', dateOfBirth: '', gender: '', globalEmployeeId: '', effectiveFrom: '', retroactiveReason: '',
};

export const WorkerFormPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const personId = searchParams.get('worker') ?? undefined;
  const isEdit = Boolean(personId);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [reference, setReference] = useState<ReferenceData | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isTerminated, setIsTerminated] = useState(false);
  // HR-mastered custom fields are not edited here but must be carried forward on update, else the
  // backend's new effective-dated PersonDetails version would drop them (see PR review P1).
  const [loadedCustomFields, setLoadedCustomFields] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let ignore = false;
    (async () => {
      const ref = await getReferenceData();
      if (!ignore) setReference(ref);
    })();
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    if (!personId) return undefined;
    let ignore = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      const result = await getWorker(personId);
      if (ignore) return;
      if (result.ok && result.data) {
        const p = result.data;
        setForm({
          title: p.title ?? '', legalFirstName: p.legalFirstName ?? '', legalMiddleName: p.legalMiddleName ?? '',
          legalLastName: p.legalLastName ?? '', preferredFirstName: p.preferredFirstName ?? '', pronouns: p.pronouns ?? '',
          dateOfBirth: p.dateOfBirth?.slice(0, 10) ?? '', gender: p.gender ?? '', globalEmployeeId: p.globalEmployeeId ?? '',
          effectiveFrom: '', retroactiveReason: '',
        });
        setIsTerminated(p.status === 'TERMINATED');
        setLoadedCustomFields(p.intellihrCustomFields);
      } else {
        setLoadError(result.message ?? 'Failed to load this worker.');
      }
      setLoading(false);
    })();
    return () => { ignore = true; };
  }, [personId]);

  const set = (key: keyof FormState) => (value: string) => setForm((f) => ({ ...f, [key]: value }));

  const titleOptions: SelectFieldOption[] = [{ label: '— None —', value: '' }, ...(reference?.titles ?? []).map((t) => ({ label: t, value: t }))];
  const genderOptions: SelectFieldOption[] = (reference?.genders ?? []).map((g) => ({ label: GENDER_LABELS[g as Gender] ?? g, value: g }));

  const showRetroReason = isRetroactive(form.effectiveFrom || undefined);

  const handleSubmit = async () => {
    setSubmitError(null);
    const validationErrors = validatePersonDetail({
      legalFirstName: form.legalFirstName, legalLastName: form.legalLastName, dateOfBirth: form.dateOfBirth,
      gender: form.gender, title: form.title || undefined, effectiveFrom: form.effectiveFrom || undefined,
      retroactiveReason: form.retroactiveReason || undefined,
    });
    setErrors(validationErrors);
    if (hasErrors(validationErrors)) return;

    setSaving(true);
    const payload: CreatePersonRequest = {
      title: trimOrUndefined(form.title),
      legalFirstName: form.legalFirstName.trim(),
      legalMiddleName: trimOrUndefined(form.legalMiddleName),
      legalLastName: form.legalLastName.trim(),
      preferredFirstName: trimOrUndefined(form.preferredFirstName),
      pronouns: trimOrUndefined(form.pronouns),
      dateOfBirth: form.dateOfBirth,
      gender: form.gender,
      effectiveFrom: trimOrUndefined(form.effectiveFrom),
      retroactiveReason: trimOrUndefined(form.retroactiveReason),
    };

    const result = isEdit && personId
      ? await updateWorkerDetail(personId, { ...payload, intellihrCustomFields: loadedCustomFields ?? undefined })
      : await createWorker({ ...payload, globalEmployeeId: trimOrUndefined(form.globalEmployeeId) });
    setSaving(false);

    if (result.ok) {
      const newId = result.data?.personId ?? personId;
      navigate(newId ? `/workers/worker-record/personal?worker=${newId}` : '/workers/worker-list');
    } else {
      setSubmitError(result.message ?? 'Could not save this worker.');
    }
  };

  if (loading) {
    return (
      <>
        <PageTitle title={isEdit ? 'Edit worker' : 'Add a new worker'} />
        <div className="mgo-cluster mgo-gap-s mgo-align-center" style={{ padding: 'var(--mgo-space-60)' }}>
          <Spinner /> <span className="mgo-text-s mgo-color-muted">Loading…</span>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle
        title={isEdit ? 'Edit worker' : 'Add a new worker'}
        subtitle={isEdit ? 'Update this person’s identity details.' : 'Create a new person (worker) record.'}
        actions={<Button variant="tertiary" label="Back to workers" onClick={() => navigate('/workers/worker-list')} />}
      />

      {loadError && <Callout tone="critical" title="Couldn't load worker">{loadError}</Callout>}
      {isTerminated && <Callout tone="caution" title="Terminated worker">This worker is terminated and cannot be edited.</Callout>}
      {submitError && <Callout tone="critical" title="Couldn't save">{submitError}</Callout>}

      <div className="mgo-background-raised mgo-border-neutral mgo-radius-strong" style={{ padding: 'var(--mgo-space-60)', marginTop: 'var(--mgo-space-40)' }}>
        <div className="mgo-stack mgo-gap-l">
          <h2 className="mgo-text-m mgo-weight-strong" style={{ margin: 0 }}>Identity</h2>
          <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-l">
            <MangoSelectField label="Title" value={form.title} options={titleOptions} onChange={set('title')} disabled={isTerminated} error={errors.title} />
            <div />
            <TextField label="Legal first name" required value={form.legalFirstName} disabled={isTerminated} onChange={(e) => set('legalFirstName')(e.target.value)} status={errors.legalFirstName ? 'critical' : undefined} statusText={errors.legalFirstName} />
            <TextField label="Legal middle name" value={form.legalMiddleName} disabled={isTerminated} onChange={(e) => set('legalMiddleName')(e.target.value)} />
            <TextField label="Legal last name" required value={form.legalLastName} disabled={isTerminated} onChange={(e) => set('legalLastName')(e.target.value)} status={errors.legalLastName ? 'critical' : undefined} statusText={errors.legalLastName} />
            <TextField label="Preferred first name" value={form.preferredFirstName} disabled={isTerminated} onChange={(e) => set('preferredFirstName')(e.target.value)} />
            <TextField label="Pronouns" placeholder="e.g. she/her" value={form.pronouns} disabled={isTerminated} onChange={(e) => set('pronouns')(e.target.value)} />
            <TextField label="Date of birth" type="date" required value={form.dateOfBirth} disabled={isTerminated} onChange={(e) => set('dateOfBirth')(e.target.value)} status={errors.dateOfBirth ? 'critical' : undefined} statusText={errors.dateOfBirth} />
            <MangoSelectField label="Gender" required value={form.gender} options={genderOptions} onChange={set('gender')} disabled={isTerminated} error={errors.gender} />
            {!isEdit && <TextField label="Global employee ID" value={form.globalEmployeeId} onChange={(e) => set('globalEmployeeId')(e.target.value)} helpText="Optional system-wide identifier." />}
          </div>

          <h2 className="mgo-text-m mgo-weight-strong" style={{ margin: 0 }}>Effective date</h2>
          <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-l">
            <TextField label="Effective from" type="date" value={form.effectiveFrom} disabled={isTerminated} onChange={(e) => set('effectiveFrom')(e.target.value)} helpText="Leave blank to take effect now." />
            <RetroactiveReasonField show={showRetroReason} value={form.retroactiveReason} onChange={set('retroactiveReason')} error={errors.retroactiveReason} disabled={isTerminated} />
          </div>

          <div className="mgo-cluster mgo-justify-end mgo-gap-s">
            <Button variant="secondary" label="Cancel" onClick={() => navigate('/workers/worker-list')} />
            <Button variant="primary" label={isEdit ? 'Save changes' : 'Create worker'} loading={saving} disabled={isTerminated} onClick={handleSubmit} />
          </div>
        </div>
      </div>
    </>
  );
};
